import _ from 'lodash'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useResizeDetector } from 'react-resize-detector'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import Wavesurfer from 'wavesurfer.js'
import MinimapPlugin, { MinimapPluginOptions } from 'wavesurfer.js/dist/plugins/minimap'
import RegionsPlugin, { Region } from 'wavesurfer.js/dist/plugins/regions'
import TimelinePlugin, { TimelinePluginOptions } from 'wavesurfer.js/dist/plugins/timeline'

import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TextField from '@/components/formik/fields/TextField'
import CheckboxInput from '@/components/ui/inputs/CheckboxInput'

type Annotation = {
  id: string
  start: number
  end: number
  note: string
}

export type AudioRecordingData = {
  annotations?: Annotation[]
  /**
   * the key content
   */
  key: string
  /**
   * the file name content
   */
  filename: string
  /**
   * the source of file
   */
  url: string
}

export type AudioRecordingTableProps = {
  /**
   * The table data.
   * @type Array<{ key: string, filename: string, url: string }>
   */
  data?: AudioRecordingData[]
  /**
   * Callback fired when a recording is selected
   * @param {AudioRecordingData} file
   */
  onSelect?: (file: AudioRecordingData) => void
  onDeleteAnnotation?: (annotationId: string, recordingFile: AudioRecordingData) => void
  onUpdateAnnotation?: (annotation: Annotation, recordingFile: AudioRecordingData) => void
}

const PLUGIN_MAP = {
  regions: RegionsPlugin,
  minimap: MinimapPlugin,
  timeline: TimelinePlugin,
} as const

type Plugins = {
  regions: RegionsPlugin | null
  minimap: MinimapPlugin | null
  timeline: TimelinePlugin | null
}

const waveformId = 'waveform'
const waveMinimapId = 'wave-minimap'
const waveTimelineId = 'wave-timeline'

const minimapPluginOptions: MinimapPluginOptions = {
  container: `#${waveMinimapId}`,
  waveColor: '#777',
  progressColor: '#222',
  height: 30,
}
const timelinePluginOptions: TimelinePluginOptions = { container: `#${waveTimelineId}`, timeInterval: 1 }

export function AudioRecordingTable(props: AudioRecordingTableProps) {
  const { data = [], onSelect, onDeleteAnnotation, onUpdateAnnotation } = props
  const { t } = useTranslation()

  const { height: audioVisualizerWrapperHeight, ref: audioVisualizerWrapperRef } = useResizeDetector()

  const pluginsRef = useRef<Plugins>({ minimap: null, regions: null, timeline: null })

  const initialPluginSelections = useMemo(
    () =>
      _.mapValues(PLUGIN_MAP, (plugin) => {
        switch (plugin.name) {
          case MinimapPlugin.name:
            return false
          default:
            return true
        }
      }),
    []
  )

  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [currentAnnotationId, setCurrentAnnotationId] = useState<string>()
  const [currentFile, setCurrentFile] = useState<AudioRecordingData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [loop, setLoop] = useState(true)
  const [pluginSelections, setPluginSelections] = useState(initialPluginSelections)
  const [wavesurfer, setWavesurfer] = useState<Wavesurfer | null>(null)

  const initialAnnotationValues = useMemo(() => {
    const currentAnnotation = _.find(annotations, ['id', currentAnnotationId])
    return {
      id: currentAnnotationId,
      start: currentAnnotation?.start ?? 0,
      end: currentAnnotation?.end ?? 0,
      note: currentAnnotation?.note ?? '',
    }
  }, [annotations, currentAnnotationId])

  const regionFormValidationSchema = useMemo(
    () =>
      yup.object({
        start: yup.number().required(),
        end: yup.number().required(),
        note: yup.string(),
      }),
    []
  )

  useEffect(() => {
    if (!wavesurfer) return

    const pluginIds = Object.keys(pluginSelections) as Array<keyof typeof PLUGIN_MAP>
    for (const pluginId of pluginIds) {
      pluginsRef.current[pluginId]?.destroy()
      let activate = pluginSelections[pluginId]
      if (!activate && pluginId === 'regions') setCurrentAnnotationId(undefined)
      else if (activate) {
        let plugin: MinimapPlugin | RegionsPlugin | TimelinePlugin | null = null
        switch (pluginId) {
          case 'regions':
            const annotations = currentFile?.annotations ?? []
            setAnnotations(annotations)
            plugin = wavesurfer.registerPlugin(PLUGIN_MAP[pluginId].create())
            for (const annotation of annotations) {
              plugin.addRegion({
                id: annotation.id,
                start: annotation.start,
                end: annotation.end,
              })
            }
            let activeRegion: Region | null = null
            plugin.enableDragSelection({})
            plugin.on('region-created', (region) => {
              setAnnotations((prevAnnotations) =>
                prevAnnotations.concat({
                  id: region.id,
                  start: region.start,
                  end: region.end,
                  note: '',
                })
              )
              setCurrentAnnotationId(region.id)
            })
            plugin.on('region-updated', (region) => {
              setAnnotations((prevAnnotation) =>
                prevAnnotation.map((prevAnnotation) =>
                  prevAnnotation.id === region.id ? { ...prevAnnotation, start: region.start, end: region.end } : prevAnnotation
                )
              )
            })
            plugin.on('region-clicked', (region, event) => {
              event.stopPropagation()
              activeRegion = region
              setCurrentAnnotationId(region.id)
              region.play()
            })
            plugin.on('region-in', (region) => {
              activeRegion = region
              setCurrentAnnotationId(region.id)
            })
            plugin.on('region-out', (region) => {
              if (activeRegion === region) {
                if (loop) {
                  region.play()
                } else {
                  activeRegion = null
                  setCurrentAnnotationId(undefined)
                }
              }
            })
            wavesurfer.on('interaction', () => {
              activeRegion = null
            })
            break
          case 'timeline':
            plugin = wavesurfer.registerPlugin(PLUGIN_MAP[pluginId].create(timelinePluginOptions))
            break
        }
        if (plugin) {
          pluginsRef.current = _.merge(pluginsRef.current, { [pluginId]: plugin })
        }
      }
    }
  }, [currentFile, loop, pluginSelections, wavesurfer])

  useLayoutEffect(() => {
    const ws = Wavesurfer.create({
      container: `#${waveformId}`,
      waveColor: 'blue',
      progressColor: 'grey',
      mediaControls: true,
      normalize: true,
      height: 160,
      plugins: [MinimapPlugin.create(minimapPluginOptions)],
    })

    ws.on('play', () => {
      setIsPlaying(true)
    })

    ws.on('pause', () => {
      setIsPlaying(false)
    })

    ws.on('finish', () => {
      setIsPlaying(false)
    })

    setWavesurfer(ws)
  }, [])

  const handleChangeAudioRecording = (file: AudioRecordingData) => () => {
    if (!currentFile || currentFile.filename !== file.filename) {
      setIsLoading(true)
      setIsPlaying(false)
      wavesurfer?.empty()
      wavesurfer
        ?.load(file.url)
        .then(() => {
          setCurrentAnnotationId(undefined)
          setCurrentFile(file)
          onSelect?.(file)
        })
        .catch(console.warn)
        .finally(() => {
          setIsLoading(false)
        })
    }
  }

  const handleDeleteRegion = () => {
    const region = _.find(pluginsRef.current.regions?.getRegions(), ['id', currentAnnotationId])
    if (!region) return
    region.remove()
    wavesurfer?.stop()
    setCurrentAnnotationId(undefined)
    if (currentFile && _.findIndex(currentFile.annotations, (annotation) => annotation.id === region.id) !== -1) {
      onDeleteAnnotation?.(region.id, currentFile)
    }
  }

  const handlePlayPause = () => {
    wavesurfer?.playPause()
  }

  const handleSelectPlugin = (pluginId: keyof typeof PLUGIN_MAP) => () => {
    let activate = !pluginSelections[pluginId]
    setPluginSelections({ ...pluginSelections, [pluginId]: activate })
  }

  const handleStop = () => {
    wavesurfer?.stop()
  }

  const handleUpdateRegion = (values: typeof initialAnnotationValues) => {
    const region = _.find(pluginsRef.current.regions?.getRegions(), ['id', values.id])
    if (!region) return
    region.setOptions({
      start: Number(values.start),
      end: Number(values.end),
    })
    if (currentFile) {
      onUpdateAnnotation?.({ id: region.id, start: Number(values.start), end: Number(values.end), note: values.note }, currentFile)
    }
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Grid container>
        <Grid maxHeight={audioVisualizerWrapperHeight ? audioVisualizerWrapperHeight + 30 : undefined} item xs={12} sm={4}>
          <Paper sx={{ height: '100%', borderRadius: 0, overflow: 'scroll' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('component.ui.audioRecordingTable.recordingList.title')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map(({ annotations, key, filename, url }: AudioRecordingData, index: number) => (
                    <TableRow
                      key={key}
                      onClick={handleChangeAudioRecording({ annotations, key, filename, url })}
                      sx={[
                        {
                          cursor: 'pointer',
                        },
                        filename === currentFile?.filename && { bgcolor: '#dfedfb' },
                      ]}
                    >
                      <TableCell sx={[index === data.length - 1 && { borderBottom: 'none' }]}>{filename}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Stack ref={audioVisualizerWrapperRef} sx={{ padding: '15px 20px' }} spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" component="div">
                {t('component.ui.audioRecordingTable.recordingDetail.title')}
              </Typography>
              <PopupState popupId="menu-plugins" variant="popover">
                {(popupState) => (
                  <Box>
                    <IconButton {...bindTrigger(popupState)}>
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      {...bindMenu(popupState)}
                      disableAutoFocusItem
                      variant="menu"
                      anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      {_.map(PLUGIN_MAP, (_plugin, pluginId: keyof typeof PLUGIN_MAP) => (
                        <MenuItem key={pluginId} onClick={handleSelectPlugin(pluginId)}>
                          <ListItemIcon>
                            <Checkbox edge="start" checked={!!pluginSelections[pluginId]} disableRipple />
                          </ListItemIcon>
                          <ListItemText>{t(`component.ui.audioRecordingTable.recordingDetail.pluginMenu.${pluginId}.label`)}</ListItemText>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                )}
              </PopupState>
            </Stack>
            <Stack>
              <Box
                component="div"
                id={waveformId}
                sx={[
                  {
                    background: '#FCFCFC',
                    height: '160px',
                    position: 'relative',
                    overflow: 'auto hidden',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                  },
                  !currentFile && {
                    '&::before': {
                      content: '""',
                      width: '100%',
                      height: '2px',
                      background: 'blue',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                    },
                  },
                ]}
              />
              <Box id={waveMinimapId} sx={{ display: pluginSelections.minimap ? 'block' : 'none' }} />
              <Box id={waveTimelineId} />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Button disabled={isLoading} disableElevation variant="media" onClick={handleStop} sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                {t('component.ui.audioRecordingTable.recordingDetail.playerButton.stop.label')}
              </Button>
              <Button
                disabled={isLoading}
                disableElevation
                variant="media"
                onClick={handlePlayPause}
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                {t(`component.ui.audioRecordingTable.recordingDetail.playerButton.${isPlaying ? 'pause' : 'play'}.label`)}
              </Button>
            </Stack>
            {currentAnnotationId && (
              <Stack>
                <CheckboxInput
                  checked={loop}
                  label={t('component.ui.audioRecordingTable.recordingAnnotation.loop.label')}
                  onChange={() => setLoop((prevLoop) => !prevLoop)}
                />
                <Formik enableReinitialize initialValues={initialAnnotationValues} onSubmit={handleUpdateRegion} validationSchema={regionFormValidationSchema}>
                  {(formikProps) => (
                    <Form onSubmit={formikProps.handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField fullWidth label={t('field.start.label')} name="start" />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth label={t('field.end.label')} name="end" />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label={t('field.note.label')} multiline name="note" rows={3} />
                        </Grid>
                      </Grid>
                      <Stack direction="row" alignItems="center" justifyContent="end" spacing={1} mt={2}>
                        <Button disabled={!formikProps.isValid} type="submit" variant="contained">
                          {t('common:button.save.label')}
                        </Button>
                        <Button onClick={handleDeleteRegion} variant="neutral">
                          {t('common:button.delete.label')}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default AudioRecordingTable
